<template>
  <div>
    <!-- Questions collapse button -->
    <b-button
      block
      variant="light"
      class="mt-4 d-flex align-items-center justify-content-center"
      @click.prevent="questionsCollapsed = !questionsCollapsed"
      :aria-expanded="questionsCollapsed ? 'true' : 'false'">
      <span>Questions</span>
      <b-icon-caret-up-fill v-if="questionsCollapsed" class="ml-2" font-scale="1.5" />
      <b-icon-caret-down-fill v-else class="ml-2" font-scale="1.5" />
    </b-button>

    <!-- Questions content -->
    <b-collapse v-model="questionsCollapsed" class="mt-3" variant="light">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Questions list</h2>
        <div>
          <b-button variant="primary" @click="showQuestionForm">
            <b-icon-plus></b-icon-plus>
            <span class="ml-1">Create a question</span>
          </b-button>
          <b-button variant="outline-info" @click="openImportModal" class="ml-2">
            <b-icon-download></b-icon-download>
            <span class="ml-1">Import questions</span>
          </b-button>
          <b-button variant="outline-info" @click="exportQuestions" class="ml-2">
            <b-icon-upload></b-icon-upload>
            <span class="ml-1">Export questions</span>
          </b-button>
          <b-dropdown
            id="select-dropdown"
            variant="outline-info"
            right
            :text="`Selected (${selectedItemsIds.length})`"
            class="ml-2"
            @show="loadSelectionCounts">
            <b-dropdown-item @click="selectPageItems" :disabled="loadingSelection">
              Select page ({{ questions.length }})
            </b-dropdown-item>
            <b-dropdown-item @click="selectTemporaryItems" :disabled="loadingSelection">
              Select temporary items
              <b-spinner small v-if="loadingSelection"></b-spinner>
              <span v-else-if="selectionCounts.temporary !== null">({{ selectionCounts.temporary }})</span>
              <span v-else>(?)</span>
            </b-dropdown-item>
            <b-dropdown-item @click="selectAllItems" :disabled="loadingSelection">
              Select all
              <b-spinner small v-if="loadingSelection"></b-spinner>
              <span v-else-if="selectionCounts.all !== null">({{ selectionCounts.all }})</span>
              <span v-else>(?)</span>
            </b-dropdown-item>
            <b-dropdown-item @click="cancelSelectItems" :disabled="loadingSelection">Cancel selection</b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item
              variant="warning"
              :disabled="!canCancelSelected || loadingSelection"
              @click="confirmBulkCancel">
              Cancel ({{ selectedItemsIds.length }})
            </b-dropdown-item>
            <b-dropdown-item
              variant="danger"
              :disabled="selectedItemsIds.length === 0 || loadingSelection"
              @click="confirmBulkDelete">
              Delete ({{ selectedItemsIds.length }})
            </b-dropdown-item>
          </b-dropdown>
          <input type="file" ref="fileInput" accept=".xlsx" style="display: none" @change="handleFileUpload" />
        </div>
      </div>
      <p>
        Questions in
        <b-badge variant="success">green</b-badge>
        will be added. Questions in
        <b-badge variant="warning">yellow</b-badge>
        will be edited. Questions in
        <b-badge variant="danger">red</b-badge>
        will be deleted.
      </p>

      <hr />

      <!-- Filters -->
      <div class="mt-3">
        <QuestionsListFilters
          :themes="themesList"
          :types="typesList"
          :levels="levelsList"
          @apply="applyFilters"
          @reset="resetFilters" />
      </div>

      <b-table
        striped
        hover
        :fields="questionsFields"
        :items="questionsItems"
        :per-page="perPage"
        :current-page="1"
        show-empty>
        <template #empty>
          <div class="d-flex align-items-center justify-content-center my-2">
            <span v-if="questionsLoading">
              <b-spinner></b-spinner>
            </span>
            <span v-else>No questions found</span>
          </div>
        </template>

        <!-- Selected field -->
        <template v-slot:cell(selected)="data">
          <input type="checkbox" v-model="selected_list[data.item.id]" :value="data.item.id" />
        </template>

        <!-- Theme field -->
        <template v-slot:cell(theme)="data">
          {{ formatThemesForDisplay(data.item.theme) }}
        </template>

        <!-- Response field -->
        <template v-slot:cell(response)="data">
          <template v-if="data.item.type === 'Binaire'">
            <b-badge variant="success" v-if="data.item.boolean">True</b-badge>
            <b-badge variant="danger" v-else>False</b-badge>
          </template>
          <template v-else>
            {{ data.item.response_ok }}
            <template v-if="data.item.response_approx_unit">
              <span class="text-muted small">({{ data.item.response_approx_unit }})</span>
            </template>
          </template>
        </template>

        <!-- Language field -->
        <template v-slot:cell(language)="data">
          <b-badge variant="info">{{ getLanguageLabel(data.item.language) }}</b-badge>
        </template>

        <!-- Enfant field -->
        <template v-slot:cell(enfant)="data">
          <b-badge variant="success" v-if="data.item.enfant">Yes</b-badge>
          <b-badge variant="light" v-else>No</b-badge>
        </template>

        <!-- Familial field -->
        <template v-slot:cell(familial)="data">
          <b-badge variant="success" v-if="data.item.familial">Yes</b-badge>
          <b-badge variant="light" v-else>No</b-badge>
        </template>

        <!-- Normal field -->
        <template v-slot:cell(normal)="data">
          <b-badge variant="success" v-if="data.item.normal">Yes</b-badge>
          <b-badge variant="light" v-else>No</b-badge>
        </template>

        <!-- Audio field -->
        <template v-slot:cell(audio)="data">
          <template v-if="data.item.mp3_question_url">
            <b-button size="sm" variant="info" v-if="audio_playing != data.item.id" @click="playAudio(data.item.id)">
              <b-icon-play-fill />
            </b-button>
            <b-button
              size="sm"
              variant="info"
              v-if="audio_playing && audio_playing == data.item.id"
              @click="stopAudio(data.item.id)">
              <b-icon-pause />
            </b-button>
            <audio
              :id="'audio_' + data.item.id"
              :src="data.item.mp3_question_url"
              preload="none"
              @ended="stopAudio(data.item.id)">
              <source :src="data.item.mp3_question_url" type="audio/mpeg" />
              <track kind="captions" />
              Your browser does not support the audio element.
            </audio>
          </template>
        </template>

        <!-- Image field -->
        <template v-slot:cell(image)="data">
          <b-img
            v-if="data.item.custom_image_url"
            thumbnail
            :src="data.item.custom_image_url"
            @click="openImageModal(data.item.custom_image_url)"
            style="max-width: 50px" />
        </template>

        <!-- Actions field -->
        <template v-slot:cell(actions)="data">
          <div class="d-flex justify-content-end">
            <template v-if="data.item._rowVariant === 'danger' || data.item._rowVariant === 'warning'">
              <b-button size="sm" variant="warning" @click="cancelTmpQuestion(data.item)">
                <b-icon-arrow-counterclockwise></b-icon-arrow-counterclockwise>
                <span class="d-none d-lg-inline">Cancel</span>
              </b-button>
            </template>
            <template v-if="!data.item._rowVariant || data.item._rowVariant !== 'danger'">
              <b-button size="sm" variant="primary" @click="editQuestion(data.item)" class="ml-2">
                <b-icon-pencil-square></b-icon-pencil-square>
                <span class="d-none d-lg-inline">Edit</span>
              </b-button>
              <b-button size="sm" variant="danger" class="ml-2" @click="confirmDelete(data.item)">
                <b-icon-trash></b-icon-trash>
                <span class="d-none d-lg-inline">Delete</span>
              </b-button>
            </template>
          </div>
        </template>
      </b-table>

      <div class="d-flex justify-content-between align-items-center mt-3">
        <div>
          <span class="text-muted">{{ totalRows }} question{{ totalRows > 1 ? 's' : '' }}</span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="center"
          aria-controls="questions-table"
          @change="handlePageChange"></b-pagination>
        <b-form-select v-model="perPage" :options="perPageOptions" class="w-auto"></b-form-select>
      </div>

      <QuestionForm
        :themes-list="themesList"
        :show.sync="showModal"
        :loading="formLoading"
        :question-to-edit="selectedQuestion"
        :errors="formErrors"
        :general-filters="generalFilters"
        @hidden="handleModalHidden"
        @submit="handleQuestionSubmit" />
    </b-collapse>

    <!-- Ajouter la modal de confirmation de suppression -->
    <b-modal
      v-model="showDeleteModal"
      title="Delete confirmation"
      ok-title="Delete"
      ok-variant="danger"
      cancel-title="Cancel"
      footer-class="d-flex flex-row-reverse justify-content-start"
      @ok="handleDelete">
      <p>Are you sure you want to delete the following question?</p>
      <p>
        <b-badge variant="primary">{{ selectedQuestion?.id }}</b-badge>
        {{ selectedQuestion?.question }}
      </p>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="danger" @click="ok()" :disabled="deleteLoading">
          <b-spinner small class="mr-2" v-if="deleteLoading"></b-spinner>
          <span>{{ deleteLoading ? 'Deleting...' : 'Delete' }}</span>
        </b-button>
        <b-button variant="secondary" @click="cancel()" :disabled="deleteLoading">Cancel</b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-image"
      size="lg"
      ref="modalImage"
      content-class="text-center"
      :hide-header="true"
      :hide-footer="true"
      :centered="true">
      <b-img :src="modalImage" alt="image" class="w-100" />
    </b-modal>

    <!-- Modal d'import de questions -->
    <ImportQuestionsModal
      ref="importQuestionsModal"
      :language="generalFilters.language"
      @success="refreshData"></ImportQuestionsModal>
  </div>
</template>

<script>
  import questionService from '@/services/questionService'
  import QuestionForm from '@/components/QuestionForm.vue'
  import { formatThemesForDisplay, parseThemesFromInput } from '@/utils/themeUtils'
  import { BIconPlayFill, BIconPause } from 'bootstrap-vue'
  import ImportQuestionsModal from './ImportQuestionsModal.vue'
  import QuestionsListFilters from './QuestionsListFilters.vue'
  import { getLanguageLabels } from '@/constants/languages'

  export default {
    name: 'QuestionsSection',
    components: {
      QuestionForm,
      BIconPlayFill,
      BIconPause,
      ImportQuestionsModal,
      QuestionsListFilters,
    },
    props: {
      // Propriétés nécessaires
      themesList: {
        type: Array,
        default: () => [],
      },
      typesList: {
        type: Array,
        default: () => [],
      },
      levelsList: {
        type: Array,
        default: () => [],
      },
      // Filtres généraux
      generalFilters: {
        type: Object,
        default: () => ({
          versions: [],
          language: '',
        }),
      },
    },
    data() {
      return {
        isInitialized: false,
        questionsCollapsed: this.getQuestionsCollapsedState(),
        filters: {
          search: '',
          theme: '',
          type: '',
          level: '',
          audio: '',
        },
        questions: [],
        questionsFields: [
          {
            key: 'selected',
            label: '',
            tdClass: 'text-center',
            thClass: 'text-center',
          },
          { key: 'id', label: 'ID' },
          { key: 'question', label: 'Question' },
          { key: 'theme', label: 'Topic' },
          { key: 'type', label: 'Type' },
          { key: 'response', label: 'Answer' },
          { key: 'language', label: 'Language' },
          { key: 'enfant', label: 'Children' },
          { key: 'familial', label: 'Family' },
          { key: 'normal', label: 'Normal' },
          { key: 'image', label: 'Image' },
          { key: 'audio', label: 'Audio' },
          { key: 'actions', label: 'Actions' },
        ],
        questionsLoading: true,
        showModal: false,
        formLoading: false,
        showDeleteModal: false,
        deleteLoading: false,
        selectedQuestion: null,
        currentPage: 1,
        perPage: 10,
        totalRows: 0,
        perPageOptions: [
          { value: 5, text: '5 per page' },
          { value: 10, text: '10 per page' },
          { value: 25, text: '25 per page' },
          { value: 50, text: '50 per page' },
          { value: 100, text: '100 per page' },
        ],
        importing: false,
        importProgress: 0,
        formErrors: {},
        audio_playing: null,
        modalImage: null,
        selected_list: {},
        loadingSelection: false,
        selectionCounts: {
          all: null,
          temporary: null,
        },
      }
    },
    computed: {
      questionsItems() {
        return this.questions.map((question) => {
          let rowVariant = null
          if (question.status === 'CREATED') {
            rowVariant = 'success'
          } else if (question.status === 'UPDATED') {
            rowVariant = 'warning'
          } else if (question.status === 'DELETED') {
            rowVariant = 'danger'
          }

          return {
            ...question,
            _rowVariant: rowVariant,
          }
        })
      },
      createdQuestions() {
        return this.questions.filter((q) => q.status === 'CREATED')
      },
      updatedQuestions() {
        return this.questions.filter((q) => q.status === 'UPDATED')
      },
      deletedQuestions() {
        return this.questions.filter((q) => q.status === 'DELETED')
      },
      selectedItemsIds() {
        return Object.keys(this.selected_list).filter((id) => this.selected_list[id])
      },
      selectedTemporaryCount() {
        return this.selectedItemsIds.filter((id) => {
          const question = this.questions.find((q) => q.id === id)
          return question && question.status
        }).length
      },
      canCancelSelected() {
        return this.selectedItemsIds.length > 0
      },
    },
    mounted() {
      this.refreshData()
    },
    methods: {
      formatThemesForDisplay,
      getQuestionsCollapsedState() {
        const savedState = localStorage.getItem('questionsCollapsed')
        return savedState !== null ? JSON.parse(savedState) : true
      },
      saveQuestionsCollapsedState() {
        localStorage.setItem('questionsCollapsed', JSON.stringify(this.questionsCollapsed))
      },
      emitQuestionsUpdated() {
        this.$emit('questions-updated', {
          created: this.createdQuestions,
          updated: this.updatedQuestions,
          deleted: this.deletedQuestions,
        })
      },
      async fetchQuestions() {
        try {
          const params = {
            page: this.currentPage,
            limit: this.perPage,
            ...this.filters,
          }

          // Ajouter les filtres généraux
          if (this.generalFilters.versions && this.generalFilters.versions.length > 0) {
            params.versions = this.generalFilters.versions
          }
          if (this.generalFilters.language) {
            params.language = this.generalFilters.language
          }

          const response = await questionService.getQuestions(params)

          this.questions = response.questions
          this.totalRows = response.pagination.total

          // Initialiser la liste de sélection
          this.initializeSelection()

          // Émettre les données vers le composant parent
          if (this.isInitialized) {
            this.emitQuestionsUpdated()
          }

          return response.questions
        } catch (error) {
          console.error('Erreur lors du chargement des questions:', error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to load questions',
          })
          return []
        } finally {
          this.isInitialized = true
        }
      },
      showQuestionForm() {
        this.showModal = true
      },
      async handleQuestionSubmit(formData) {
        this.formLoading = true
        this.formErrors = {}

        try {
          if (this.selectedQuestion) {
            // Mode édition
            await questionService.updateQuestion(this.selectedQuestion.id, {
              ...formData,
              theme: parseThemesFromInput(formData.theme),
            })

            // Rafraîchir la liste
            await this.refreshData()

            this.$notify({
              type: 'success',
              title: 'Question edited',
              text: `Question "${this.selectedQuestion.id}" has been edited successfully`,
            })
          } else {
            // Mode création
            const response = await questionService.createQuestion({
              ...formData,
              theme: parseThemesFromInput(formData.theme),
            })

            // Rafraîchir la liste complète
            await this.refreshData()

            let newQuestionId = response.id
            this.$notify({
              type: 'success',
              title: 'Question created',
              text: `Question "${newQuestionId}" has been created successfully`,
            })
          }

          this.showModal = false
          this.selectedQuestion = null
        } catch (error) {
          if (error.response?.status === 422) {
            this.formErrors = error.response?.data?.errors || {}
          }

          console.error('Erreur lors de la création de la question:', error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.error || 'An error occurred while creating the question',
          })
        } finally {
          this.formLoading = false
        }
      },
      confirmDelete(question) {
        this.selectedQuestion = question
        this.showDeleteModal = true
      },
      async handleDelete(bvModalEvent) {
        bvModalEvent.preventDefault()
        this.deleteLoading = true

        try {
          await questionService.deleteQuestion(this.selectedQuestion.id)

          // Rafraîchir la liste
          await this.refreshData()

          this.$notify({
            type: 'success',
            title: 'Question deleted',
            text: `Question "${this.selectedQuestion.id}" has been deleted successfully`,
          })

          this.showDeleteModal = false
          this.selectedQuestion = null
        } catch (error) {
          console.error('Erreur lors de la suppression de la question:', error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.error || 'An error occurred while deleting the question',
          })
        } finally {
          this.deleteLoading = false
        }
      },
      editQuestion(question) {
        this.selectedQuestion = question
        this.showModal = true
      },
      handleModalHidden() {
        this.showModal = false
        this.selectedQuestion = null
      },
      handlePageChange(page) {
        this.currentPage = page
        this.refreshData()
      },
      async cancelTmpQuestion(question) {
        let notificationTitle, notificationText, errorNotificationTitle, errorNotificationText
        switch (question.status) {
          case 'CREATED':
            notificationTitle = 'Creation canceled'
            notificationText = `Creation of question "${question.id}" has been canceled`
            errorNotificationTitle = 'Error during creation cancellation'
            errorNotificationText = `An error occurred while canceling the creation of question "${question.id}"`
            break
          case 'UPDATED':
            notificationTitle = 'Edit canceled'
            notificationText = `Edit of question "${question.id}" has been canceled`
            errorNotificationTitle = 'Error during edit cancellation'
            errorNotificationText = `An error occurred while canceling the edit of question "${question.id}"`
            break
          case 'DELETED':
            notificationTitle = 'Deletion canceled'
            notificationText = `Deletion of question "${question.id}" has been canceled`
            errorNotificationTitle = 'Error during deletion cancellation'
            errorNotificationText = `An error occurred while canceling the deletion of question "${question.id}"`
            break
        }

        try {
          await questionService.cancelQuestion(question.id)

          // Rafraîchir la liste
          await this.refreshData()

          this.$notify({
            type: 'success',
            title: notificationTitle,
            text: notificationText,
          })
        } catch (error) {
          console.error(errorNotificationTitle, error)
          this.$notify({
            type: 'error',
            title: errorNotificationTitle,
            text: error.response?.data?.error || errorNotificationText,
          })
        }
      },
      async handleFileUpload(event) {
        const file = event.target.files[0]
        if (!file) return

        try {
          this.importing = true
          this.importProgress = 0

          const response = await questionService.importQuestions(file)

          // Vérifier si la réponse contient une erreur
          if (response.error) {
            throw new Error(response.error)
          }

          this.$notify({
            type: 'success',
            title: 'Import successful',
            text: `${response.added} ${response.added > 1 ? 'additions' : 'addition'}, ${response.updated} ${response.updated > 1 ? 'edits' : 'edit'}, ${response.deleted} ${response.deleted > 1 ? 'deletions' : 'deletion'}`,
          })

          // Rafraîchir la liste des questions
          await this.refreshData()
        } catch (error) {
          console.error("Erreur lors de l'import:", error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.message || error.message || 'Error during import',
          })
        } finally {
          this.importing = false
          this.importProgress = 0
          event.target.value = ''
        }
      },
      async exportQuestions() {
        try {
          const response = await questionService.exportQuestions()

          const url = window.URL.createObjectURL(new Blob([response]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'questions-export.xlsx')
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          this.$notify({
            type: 'success',
            title: 'Export successful',
            text: 'Questions have been exported successfully',
          })
        } catch (error) {
          console.error("Erreur lors de l'export:", error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to export questions',
          })
        }
      },
      refreshData() {
        this.questionsLoading = true
        // Invalider le cache des totaux
        this.selectionCounts = { all: null, temporary: null }
        this.fetchQuestions().finally(() => {
          this.questionsLoading = false
        })
      },
      applyFilters(data) {
        this.filters = data
        this.refreshData()
      },
      resetFilters() {
        this.filters = {
          search: '',
          theme: '',
          type: '',
          level: '',
          audio: '',
        }
        this.refreshData()
      },
      async playAudio(id) {
        try {
          // Arrêter l'audio précédent s'il y en a un
          if (this.audio_playing && this.audio_playing !== id) {
            this.stopAudio(this.audio_playing)
          }

          const audioElement = document.getElementById(`audio_${id}`)
          if (audioElement) {
            // Forcer le rechargement de l'élément audio pour éviter les erreurs en cache
            audioElement.load()

            // Attendre un peu pour que le chargement se fasse
            await new Promise((resolve) => setTimeout(resolve, 100))

            // Vérifier l'état de l'audio avant de jouer
            if (audioElement.error) {
              const mediaError = audioElement.error
              if (mediaError.code === mediaError.MEDIA_ERR_NETWORK) {
                throw new Error('Audio file not found on server')
              } else if (mediaError.code === mediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
                throw new Error('Audio format not supported')
              } else if (mediaError.code === mediaError.MEDIA_ERR_DECODE) {
                throw new Error('Audio decoding error')
              } else if (mediaError.code === mediaError.MEDIA_ERR_ABORTED) {
                throw new Error('Audio loading interrupted')
              }
            }
            await audioElement.play()
            this.audio_playing = id
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Erreur lors de la lecture audio:', error)

          let errorMessage = 'Unable to play audio file'

          // Analyser le message d'erreur pour déterminer le type
          if (error.message && error.message.includes('Audio file not found on server')) {
            errorMessage = 'Audio file not found on server'
          } else if (error.message && error.message.includes('Audio format not supported')) {
            errorMessage = 'Audio format not supported by your browser'
          } else if (error.message && error.message.includes('Audio decoding error')) {
            errorMessage = 'Corrupted audio file or invalid format'
          } else if (error.message && error.message.includes('Audio loading interrupted')) {
            errorMessage = 'Playback interrupted before loading completed'
          } else if (
            (error.message && error.message.includes('404')) ||
            (error.cause && error.cause.includes('404')) ||
            error.code === 404
          ) {
            errorMessage = 'Audio file not found on server'
          } else if (
            error.name === 'NotSupportedError' &&
            error.message &&
            error.message.includes('no supported source was found')
          ) {
            errorMessage = 'Audio file not found on server'
          } else if (error.name === 'NotAllowedError') {
            errorMessage = 'Playback blocked: please allow autoplay or interact with the page first'
          } else if (error.name === 'AbortError') {
            errorMessage = 'Playback interrupted before loading completed'
          } else if (error.name === 'NetworkError' || (error.message && error.message.includes('network'))) {
            errorMessage = 'Network error: unable to load audio file'
          } else if (error.name === 'NotSupportedError' || (error.message && error.message.includes('format'))) {
            errorMessage = 'Audio format not supported by your browser'
          } else if (error.message && error.message.includes('decode')) {
            errorMessage = 'Corrupted audio file or invalid format'
          } else if (error.message) {
            errorMessage = `Playback error: ${error.message}`
          }

          this.$notify({
            type: 'error',
            title: 'Audio error',
            text: errorMessage,
          })
        }
      },
      stopAudio(id) {
        const sound = document.getElementById(`audio_${id}`)
        if (sound) {
          sound.pause()
          sound.currentTime = 0
        }
        this.audio_playing = null
      },
      openImageModal(image) {
        this.modalImage = image
        this.$refs.modalImage.show()
      },
      openImportModal() {
        this.$refs.importQuestionsModal?.show()
      },
      getLanguageLabel(languageCode) {
        const labels = getLanguageLabels()
        return labels[languageCode] || labels['fr']
      },
      getCurrentFilters() {
        return {
          ...this.filters,
          versions: this.generalFilters.versions,
          language: this.generalFilters.language,
        }
      },
      async loadSelectionCounts() {
        if (this.loadingSelection) return

        this.loadingSelection = true

        try {
          const [allResponse, temporaryResponse] = await Promise.allSettled([
            questionService.getQuestionIds('all', this.getCurrentFilters()),
            questionService.getQuestionIds('temporary', this.getCurrentFilters()),
          ])

          if (allResponse.status === 'fulfilled') {
            this.selectionCounts.all = allResponse.value.data.total
          }

          if (temporaryResponse.status === 'fulfilled') {
            this.selectionCounts.temporary = temporaryResponse.value.data.total
          }
        } catch (error) {
          console.error('Erreur lors du chargement des totaux:', error)
        } finally {
          this.loadingSelection = false
        }
      },
      // Méthodes de sélection
      initializeSelection() {
        const tempList = {}
        this.questions.forEach((question) => {
          tempList[question.id] = this.selected_list[question.id] || false
        })
        this.selected_list = tempList
      },
      cancelSelectItems() {
        Object.keys(this.selected_list).forEach((key) => {
          this.selected_list[key] = false
        })
      },
      selectPageItems() {
        this.cancelSelectItems()
        this.questions.forEach((question) => {
          if (this.selected_list[question.id] !== undefined) {
            this.selected_list[question.id] = true
          }
        })
      },
      async selectTemporaryItems() {
        if (this.loadingSelection) return

        this.loadingSelection = true
        try {
          this.cancelSelectItems()
          const response = await questionService.getQuestionIds('temporary', this.getCurrentFilters())

          // Mettre à jour selected_list avec tous les IDs temporaires
          response.data.ids.forEach((id) => {
            if (this.selected_list[id] !== undefined) {
              this.selected_list[id] = true
            } else {
              // Ajouter l'ID même s'il n'est pas dans la page actuelle
              this.$set(this.selected_list, id, true)
            }
          })

          // Mettre à jour le cache
          this.selectionCounts.temporary = response.data.total

          this.$notify({
            type: 'info',
            title: 'Temporary selection',
            text: `${response.data.total} temporary question${response.data.total > 1 ? 's' : ''} selected`,
          })
        } catch (error) {
          console.error('Erreur lors de la sélection des temporaires:', error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to select temporary questions',
          })
        } finally {
          this.loadingSelection = false
        }
      },
      async selectAllItems() {
        if (this.loadingSelection) return

        this.loadingSelection = true
        try {
          this.cancelSelectItems()
          const response = await questionService.getQuestionIds('all', this.getCurrentFilters())

          // Mettre à jour selected_list avec tous les IDs
          response.data.ids.forEach((id) => {
            if (this.selected_list[id] !== undefined) {
              this.selected_list[id] = true
            } else {
              // Ajouter l'ID même s'il n'est pas dans la page actuelle
              this.$set(this.selected_list, id, true)
            }
          })

          // Mettre à jour le cache
          this.selectionCounts.all = response.data.total

          this.$notify({
            type: 'info',
            title: 'Complete selection',
            text: `${response.data.total} question${response.data.total > 1 ? 's' : ''} selected`,
          })
        } catch (error) {
          console.error('Erreur lors de la sélection complète:', error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to select all questions',
          })
        } finally {
          this.loadingSelection = false
        }
      },
      // Actions de masse
      async confirmBulkCancel() {
        if (this.selectedItemsIds.length === 0) {
          this.$notify({
            type: 'info',
            title: 'No question selected',
            text: 'Please select at least one question',
          })
          return
        }

        if (
          window.confirm(
            `Are you sure you want to cancel ${this.selectedItemsIds.length} question${this.selectedItemsIds.length > 1 ? 's' : ''}?`
          )
        ) {
          try {
            const response = await questionService.bulkActions('cancel', this.selectedItemsIds)

            this.$notify({
              type: 'success',
              title: 'Cancellation successful',
              text: response.message,
            })

            if (response.data.errors && response.data.errors.length > 0) {
              console.warn("Erreurs lors de l'annulation:", response.data.errors)
            }

            await this.refreshData()
            this.cancelSelectItems()
          } catch (error) {
            console.error(error)
            this.$notify({
              type: 'error',
              title: 'Error during cancellation',
              text: error.response?.data?.error?.message || 'An error occurred',
            })
          }
        }
      },
      async confirmBulkDelete() {
        if (this.selectedItemsIds.length === 0) {
          this.$notify({
            type: 'info',
            title: 'No question selected',
            text: 'Please select at least one question',
          })
          return
        }

        if (
          window.confirm(
            `Are you sure you want to delete ${this.selectedItemsIds.length} question${this.selectedItemsIds.length > 1 ? 's' : ''}?`
          )
        ) {
          try {
            const response = await questionService.bulkActions('delete', this.selectedItemsIds)

            this.$notify({
              type: 'success',
              title: 'Deletion successful',
              text: response.message,
            })

            if (response.data.errors && response.data.errors.length > 0) {
              console.warn('Erreurs lors de la suppression:', response.data.errors)
            }

            await this.refreshData()
            this.cancelSelectItems()
          } catch (error) {
            console.error(error)
            this.$notify({
              type: 'error',
              title: 'Error during deletion',
              text: error.response?.data?.error?.message || 'An error occurred',
            })
          }
        }
      },
    },
    watch: {
      perPage() {
        this.currentPage = 1 // Réinitialiser à la première page lors du changement de perPage
        this.refreshData()
      },
      questionsCollapsed() {
        this.saveQuestionsCollapsedState()
      },
      generalFilters: {
        handler() {
          this.currentPage = 1 // Réinitialiser à la première page lors du changement de filtres
          this.refreshData()
        },
        deep: true,
      },
    },
  }
</script>

<style scoped>
  .pagination {
    margin-bottom: 0;
  }
</style>
